// NIGHT-DIRECTSTAFF-2026-10-19 §1: type-as-you-go search on the Leads list and the Candidates list -- name, email,
// phone (any formatting), state and the free-text message -- applied WITHIN the list's current filters.
//
//   * Every word must match (AND), in any field. "pt denver" finds a PT whose message says Denver.
//   * A word with 3+ digits is a phone search: digits only, so "(720) 555", "720-555" and "+1720555" all hit 7205550101.
//     A leading country 1 is dropped from both sides.
//   * A 1-2 letter word (a state code, an initial) matches a WHOLE word only, so "co" finds Colorado's "CO" and not every
//     "company" or ".com".
//   * In-memory over what the list already holds (hundreds to low thousands of rows): no request, no index. Each row's
//     text is built once and cached on the row object (WeakMap), so typing re-reads nothing.

const s = (v) => (v === null || v === undefined ? '' : String(v)).toLowerCase();
const digitsOf = (v) => String(v || '').replace(/\D/g, '');
const national = (d) => (d.length === 11 && d.startsWith('1') ? d.slice(1) : d);

// The free-text keys forms post (the same list Needs a human reads "what they asked for" from), plus Meta's answers.
const MESSAGE_KEYS = ['message', 'notes', 'note', 'comments', 'comment', 'question', 'details', 'inquiry', 'how_can_we_help', 'essay',
  'job_title', 'job_location', 'facility_name', 'company_name', 'city', 'firstName', 'first_name', 'lastName', 'last_name'];

function payloadText(r) {
  if (!r || typeof r !== 'object') return '';
  const out = [];
  for (const k of MESSAGE_KEYS) if (typeof r[k] === 'string') out.push(r[k]);
  // Meta lead forms: field_data [{ name, values: [...] }] -- the answers are free text too
  if (Array.isArray(r.field_data)) for (const f of r.field_data) if (f && Array.isArray(f.values)) out.push(f.values.join(' '));
  return out.join(' ');
}

const cache = new WeakMap();
function indexed(row, build) {
  if (!row || typeof row !== 'object') return { text: '', phone: '' };
  let v = cache.get(row);
  if (!v) { v = build(row); cache.set(row, v); }
  return v;
}

/** A lead's searchable text: id, name, email, state, discipline, site, form and what they wrote; phone as digits. */
export function leadIndex(l) {
  return indexed(l, (x) => ({
    text: ` ${[x.id, x.name, x.email, x.state, x.discipline, x.source_site, x.form_type, x.source_page, payloadText(x.raw_payload)].map(s).join(' ')} `,
    phone: national(digitsOf(x.phone)),
  }));
}
/** A candidate's searchable text: name, email, state, specialty, stage, notes; phone as digits. */
export function candidateIndex(c) {
  return indexed(c, (x) => ({
    text: ` ${[x.name, x.email, x.state, x.specialty, x.stage, x.notes, x.city, x.recruiter].map(s).join(' ')} `,
    phone: national(digitsOf(x.phone)),
  }));
}

/** The query's words, lower case; '' -> []. */
export function queryWords(q) {
  return s(q).trim().split(/\s+/).filter(Boolean);
}

/** Does the row's phone contain these digits -- as typed, or without a leading country 1? */
const phoneHas = (phone, d) => !!phone && (phone.includes(national(d)) || (d.length > 3 && d.startsWith('1') && phone.includes(d.slice(1))));

const escapeRe = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/** One word against one row's index. */
function wordMatches(w, ix) {
  const d = digitsOf(w);
  // a phone word: only digits and phone punctuation, at least 3 digits
  if (d.length >= 3 && /^\d+$/.test(w.replace(/[().+-]/g, ''))) return phoneHas(ix.phone, d);
  if (w.length <= 2) return new RegExp(`(^|[^a-z0-9])${escapeRe(w)}([^a-z0-9]|$)`).test(ix.text);
  return ix.text.includes(w);
}

/** True when every word of q matches the row (an empty q matches everything). */
export function matchesText(ix, q) {
  // the whole query is a phone ("+1 720 555 0101", "(720) 555-0101"): one digits search, with or without the leading 1
  const whole = String(q || '').trim();
  const d = digitsOf(whole);
  if (d.length >= 3 && /^[\d\s().+-]+$/.test(whole)) return phoneHas(ix.phone, d);
  const words = queryWords(q);
  return words.every((w) => wordMatches(w, ix));
}
export const leadMatches = (l, q) => !queryWords(q).length || matchesText(leadIndex(l), q);
export const candidateMatches = (c, q) => !queryWords(q).length || matchesText(candidateIndex(c), q);
